// © 2026 Camp Promethean, LLC. All Rights Reserved.
// In-place lock screen for tier-gated modules — replaces the old
// toast-then-bounce to /pricing. The visitor keeps their bearings, sees what
// the tier actually buys, and their intent is already captured so an upgrade
// brings them straight back here. Spec: docs/upgrade-downgrade-flow-spec.md P3

import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BrandMark } from "@/components/brand/BrandMark";
import { Button } from "@/components/ui/button";
import { DashboardContent } from "@/components/layouts/DashboardContent";
import {
  PLAN_CHANGE_CONTENT,
  isPaidTier,
} from "@/content/planChangeContent";
import { trackSubscriptionEvent } from "@/lib/analytics/subscriptionEvents";
import type { NavItem } from "@/types/navigation";

interface LockedModuleProps {
  item: NavItem;
}

export function LockedModule({ item }: LockedModuleProps) {
  const navigate = useNavigate();
  const tier = isPaidTier(item.requiredTier) ? item.requiredTier : "Basic";
  const content = PLAN_CHANGE_CONTENT[tier];

  const goToPlans = () => {
    trackSubscriptionEvent("success_cta_clicked", {
      cta: "locked_module_see_plans",
      path: item.path,
      requiredTier: tier,
    });
    navigate("/pricing");
  };

  return (
    <DashboardContent className="flex min-h-[70vh] items-center justify-center">
      <div className="w-full max-w-lg animate-in fade-in slide-in-from-bottom-2 duration-500">
        <div className="overflow-hidden rounded-2xl border bg-card">
          <div className="h-[3px] w-full bg-primary/70" aria-hidden="true" />
          <div className="space-y-6 p-8 text-center">
            <BrandMark className="mx-auto h-9 w-auto opacity-80" />

            <div className="space-y-2">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-primary">
                Part of {tier}
              </p>
              <h1 className="text-2xl font-semibold tracking-tight">
                {item.label} unlocks with {tier}
              </h1>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {content.tagline}
              </p>
            </div>

            <ul className="space-y-1.5 rounded-lg border bg-muted/30 px-5 py-4 text-left">
              {content.highlights.map((highlight) => (
                <li
                  key={highlight.title}
                  className="flex items-baseline gap-2.5 text-sm text-muted-foreground"
                >
                  <span
                    className="inline-block h-px w-3 shrink-0 translate-y-[-3px] bg-primary/50"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="text-foreground">{highlight.title}</span>
                    {" — "}
                    {highlight.description}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col items-center gap-2.5">
              <Button size="lg" onClick={goToPlans}>
                See {tier} plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => navigate("/dashboard")}
              >
                Back to dashboard
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Upgrade and you&rsquo;ll land right back here.
            </p>
          </div>
        </div>
      </div>
    </DashboardContent>
  );
}
